import _ from 'lodash';
import { ASSET_PATH_SEPARATOR } from '@/main/app.constants';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { ItemPreviewWithAssetsV1 } from '@seeqdev/api/model/models';

/**
 * Extracts the ancestors information from the provided response and adds a property to each item with the
 * assets, including the formatted name that includes the ancestors.
 *
 * @param data - Response from an endpoint that includes the ancestors property
 * @returns Transformed data with items containing a new assets property that is the list of assets the
 * item belongs to.
 */
export function addAssetsProperty(data: { items?: { ancestors?: { id: string; name: string }[] }[] }): {
  items: (ItemPreviewWithAssetsV1 & { assets: any[] })[];
} {
  _.forEach(_.get(data, 'items'), (item, k) => {
    (data.items?.[k] as any).assets = [getAssetFromAncestors(item.ancestors ?? [])];
  });
  return data as any;
}

/**
 * Returns an asset object with id, name, and formattedName based on its ancestors.
 *
 * @param {Object[]} ancestors - The array of ancestors
 * @returns {Object} Transformed object ancestors new properties.
 *                   {String} .id - ID of asset dependency
 *                   {String} .name - Name of asset dependency
 *                   {String} .formattedName - Name that includes the full asset path
 */
export function getAssetFromAncestors(ancestors: { name: string; id: string }[]) {
  return _.assign(
    {},
    _.pick(_.last(ancestors), ['id', 'name']),
    { formattedName: _.map(ancestors, 'name').join(ASSET_PATH_SEPARATOR) },
    { pathComponentIds: _.map(ancestors, 'id') },
  );
}

/**
 * Helper function to take an object and format all of its key/value pairs into a single query parameter string
 *
 * @param params - Object containing the key/value pairs to use as query parameters
 * @returns a string containing all value-ful properties
 */
export function formatAsQueryString(params: Record<string, any>) {
  const searchParams = new URLSearchParams();
  _.forEach(params, (value, key) => {
    if (value !== undefined) searchParams.append(key, value);
  });
  return searchParams.toString();
}

/**
 * Helper function to get a property without throwing an error when the property does not exist
 *
 * @param id - The ID of the item
 * @param propertyName - The name of the property to get
 * @param defaultValue - The default value to return if the property does not exist
 * @returns the string value of the property or the default value
 */
export async function safeGetProperty<T>(id: string, propertyName: string, defaultValue: T): Promise<string | T> {
  try {
    const {
      data: { value },
    } = await sqItemsApi.getProperty({ id, propertyName });
    return value ?? defaultValue;
  } catch (error) {
    return defaultValue;
  }
}

/**
 * Helper function to get a property and parse it as JSON without throwing an error when the property does not exist or
 * is not valid JSON
 *
 * @param id - The ID of the item
 * @param propertyName - The name of the property to get
 * @param defaultValue - The default value to return if the property does not exist
 * @returns the string value of the property or the default value
 */
export async function safeGetJsonProperty<T>(id: string, propertyName: string, defaultValue: T): Promise<T> {
  const value = await safeGetProperty(id, propertyName, null);
  if (value) {
    try {
      return JSON.parse(value) as T;
    } catch (error: unknown) {
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
}
